/**
 * A piece of landing copy bound to a content key.
 *
 * Renders as plain text for visitors. For a signed-in admin it gains a dotted
 * underline and a pencil; clicking opens an editor with Save / Cancel / Reset.
 *
 * The editor is rendered through a portal into the `.elf` root rather than
 * inline. Several containers it appears inside — `.railtile` most of all —
 * set `overflow: hidden`, which silently clipped the whole panel (and its Save
 * button) out of existence. Portalling escapes that, and the palette variables
 * still resolve because the portal target is inside `.elf`.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader2, Pencil, RotateCcw, X } from 'lucide-react';
import { useLandingImages } from './useLandingImages';

export interface EditableTextProps {
  /** Content key, e.g. `services.title`. */
  contentKey: string;
  /** Wording shipped with the page; used until an override exists. */
  fallback: string;
  /** Element to render as. Defaults to `span`. */
  as?: keyof React.JSX.IntrinsicElements;
  className?: string;
  style?: React.CSSProperties;
  /** Edit in a textarea rather than a single-line input. */
  multiline?: boolean;
  /** Human label shown in the editor header. */
  label?: string;
  /** Render the resolved value yourself (e.g. split a list into rows). */
  children?: (value: string) => React.ReactNode;
  /** Drop the dashed underline — for controls that aren't inline prose. */
  underline?: boolean;
}

const POPOVER_WIDTH = 420;
const GUTTER = 12;

export const EditableText: React.FC<EditableTextProps> = ({
  contentKey,
  fallback,
  as: Tag = 'span',
  className,
  style,
  multiline = false,
  label,
  children,
  underline = true,
}) => {
  const { text, canEdit, setText, resetText, isTextOverridden, savingKey } = useLandingImages();
  const value = text(contentKey, fallback);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [localError, setLocalError] = useState<string | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const pencilRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLTextAreaElement & HTMLInputElement>(null);

  const saving = savingKey === contentKey;

  /** Anchor under the pencil, clamped so the panel always sits on screen. */
  const place = useCallback(() => {
    const anchor = pencilRef.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const width = Math.min(POPOVER_WIDTH, window.innerWidth - GUTTER * 2);
    const height = popRef.current?.offsetHeight ?? 220;

    let left = r.left;
    left = Math.max(GUTTER, Math.min(left, window.innerWidth - width - GUTTER));

    // Flip above the trigger when there isn't room below it.
    let top = r.bottom + 8;
    if (top + height > window.innerHeight - GUTTER) {
      top = Math.max(GUTTER, r.top - height - 8);
    }

    setPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!editing) return;
    place();
    fieldRef.current?.focus();
  }, [editing, place]);

  // Keep it anchored while the page moves under it.
  useEffect(() => {
    if (!editing) return;
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [editing, place]);

  // Close on Escape or a click outside the panel.
  useEffect(() => {
    if (!editing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setEditing(false);
    };
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (popRef.current?.contains(target)) return;
      if (pencilRef.current?.contains(target)) return;
      setEditing(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown, true);
    };
  }, [editing]);

  const open = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDraft(value);
      setLocalError(null);
      setEditing(true);
    },
    [value],
  );

  const save = useCallback(async () => {
    try {
      await setText(contentKey, draft);
      setEditing(false);
    } catch (err: any) {
      setLocalError(err?.message ?? 'Save failed');
    }
  }, [contentKey, draft, setText]);

  const restore = useCallback(async () => {
    try {
      await resetText(contentKey);
      setEditing(false);
    } catch (err: any) {
      setLocalError(err?.message ?? 'Reset failed');
    }
  }, [contentKey, resetText]);

  const rendered = children ? children(value) : value;

  // Visitors get the bare element — no wrapper, no behaviour, no layout shift.
  if (!canEdit) {
    return (
      <Tag className={className} style={style}>
        {rendered}
      </Tag>
    );
  }

  const host = pencilRef.current?.closest('.elf') ?? null;

  const editor = (
    <div
      ref={popRef}
      className="elf-etext__pop"
      style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
      onClick={(e) => e.stopPropagation()}
      onPointerEnter={(e) => e.stopPropagation()}
    >
      <div className="elf-etext__head">
        <span className="mono">{label ?? contentKey}</span>
        {multiline && <span className="elf-etext__hint">one per line</span>}
      </div>

      {multiline ? (
        <textarea
          ref={fieldRef}
          className="elf-etext__field"
          rows={Math.min(10, Math.max(3, draft.split('\n').length + 1))}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
      ) : (
        <input
          ref={fieldRef}
          className="elf-etext__field"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void save();
          }}
        />
      )}

      {localError && <span className="elf-etext__err">{localError}</span>}

      <div className="elf-etext__actions">
        <button
          type="button"
          className="elf-etext__btn elf-etext__btn--primary"
          disabled={saving}
          onClick={() => void save()}
        >
          {saving ? <Loader2 size={12} className="elf-edit__spin" /> : <Check size={12} />}
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="elf-etext__btn" onClick={() => setEditing(false)}>
          <X size={12} /> Cancel
        </button>
        {isTextOverridden(contentKey) && (
          <button
            type="button"
            className="elf-etext__btn"
            disabled={saving}
            title="Restore the original wording"
            onClick={() => void restore()}
          >
            <RotateCcw size={12} /> Reset
          </button>
        )}
      </div>
    </div>
  );

  return (
    <Tag
      className={[className, 'elf-etext', underline ? null : 'elf-etext--bare']
        .filter(Boolean)
        .join(' ')}
      style={style}
    >
      {rendered}
      <button
        ref={pencilRef}
        type="button"
        className="elf-etext__pencil"
        title={`Edit: ${label ?? contentKey}`}
        onClick={open}
      >
        <Pencil size={11} aria-hidden="true" />
      </button>
      {editing && host && createPortal(editor, host)}
    </Tag>
  );
};

export default EditableText;
